import { useState } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { LayoutDashboard, Database, FolderOpen, BarChart3 } from 'lucide-react';

const DashboardView = () => (
  <div className="dashboard-mockup bg-[#1E293B] rounded-xl overflow-hidden">
    <div className="p-6 space-y-4">
      <div className="grid grid-cols-4 gap-4">
        {[
          { label: 'Total Projects', value: '12', color: '#3B82F6' },
          { label: 'Published', value: '147', color: '#22C55E' },
          { label: 'Pending', value: '34', color: '#EAB308' },
          { label: 'Success Rate', value: '76%', color: '#EC4899' },
        ].map((item) => (
          <div key={item.label} className="kpi-card p-4">
            <p className="text-xs text-white/60 mb-1">{item.label}</p>
            <p className="text-2xl font-bold" style={{ color: item.color }}>{item.value}</p>
          </div>
        ))}
      </div>
      <div className="status-bar">
        <div className="flex h-full">
          <div className="h-full bg-green-500" style={{ width: '55%' }}></div>
          <div className="h-full bg-blue-500" style={{ width: '20%' }}></div>
          <div className="h-full bg-yellow-500" style={{ width: '15%' }}></div>
          <div className="h-full bg-red-500" style={{ width: '10%' }}></div>
        </div>
      </div>
    </div>
  </div>
);

const DirectoryView = () => (
  <div className="dashboard-mockup bg-[#1E293B] rounded-xl overflow-hidden">
    <div className="p-4">
      <div className="flex gap-2 mb-4">
        {['All', 'Guest Post', 'Forum', 'Directory'].map((filter) => (
          <span
            key={filter}
            className={`px-3 py-1 rounded-full text-xs ${
              filter === 'All' ? 'bg-blue-500 text-white' : 'bg-white/10 text-white/60'
            }`}
          >
            {filter}
          </span>
        ))}
      </div>
      <table className="data-table text-sm">
        <thead>
          <tr>
            <th>Website</th>
            <th>DA</th>
            <th>Type</th>
            <th>Link Type</th>
          </tr>
        </thead>
        <tbody>
          {[
            { site: 'medium.com', da: 96, type: 'Blog', link: 'DoFollow' },
            { site: 'dev.to', da: 87, type: 'Profile', link: 'NoFollow' },
            { site: 'hashnode.dev', da: 72, type: 'Blog', link: 'DoFollow' },
            { site: 'linkedin.com', da: 98, type: 'Article', link: 'NoFollow' },
          ].map((row) => (
            <tr key={row.site}>
              <td className="text-white">{row.site}</td>
              <td className="text-green-400">{row.da}</td>
              <td className="text-white/60">{row.type}</td>
              <td>
                <span className={`px-2 py-0.5 rounded text-xs ${
                  row.link === 'DoFollow' ? 'bg-green-500/20 text-green-400' : 'bg-gray-500/20 text-gray-400'
                }`}>
                  {row.link}
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
);

const ProjectView = () => (
  <div className="dashboard-mockup bg-[#1E293B] rounded-xl overflow-hidden">
    <div className="p-4">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h4 className="text-white font-medium">Homepage SEO Campaign</h4>
          <p className="text-white/60 text-sm">Target: example.com | Anchor: "best SEO tool"</p>
        </div>
        <span className="px-3 py-1 bg-green-500/20 text-green-400 rounded-full text-xs">Active</span>
      </div>
      <table className="data-table text-sm">
        <thead>
          <tr>
            <th>Backlink Source</th>
            <th>Status</th>
            <th>Date</th>
          </tr>
        </thead>
        <tbody>
          {[
            { source: 'medium.com/@article', status: 'Published', date: 'Dec 15' },
            { source: 'dev.to/post/seo-tips', status: 'Submitted', date: 'Dec 14' },
            { source: 'hashnode.dev/blog', status: 'Pending', date: 'Dec 13' },
            { source: 'linkedin.com/pulse', status: 'Rejected', date: 'Dec 12' },
          ].map((row) => (
            <tr key={row.source}>
              <td className="text-white">{row.source}</td>
              <td>
                <span className={`status-badge status-${row.status.toLowerCase()}`}>
                  {row.status}
                </span>
              </td>
              <td className="text-white/60">{row.date}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
);

const AnalyticsView = () => (
  <div className="dashboard-mockup bg-[#1E293B] rounded-xl overflow-hidden">
    <div className="p-4 space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div className="kpi-card p-4">
          <p className="text-xs text-white/60 mb-1">Total Links Built</p>
          <p className="text-3xl font-bold text-white">1,247</p>
          <p className="text-xs text-green-400">+12% this month</p>
        </div>
        <div className="kpi-card p-4">
          <p className="text-xs text-white/60 mb-1">Avg. Success Rate</p>
          <p className="text-3xl font-bold text-white">78%</p>
          <p className="text-xs text-green-400">+5% vs last month</p>
        </div>
      </div>
      <div className="space-y-2">
        {[
          { project: 'Homepage Campaign', rate: 85, color: '#22C55E' },
          { project: 'Blog SEO Push', rate: 72, color: '#3B82F6' },
          { project: 'Product Pages', rate: 68, color: '#8B5CF6' },
        ].map((item) => (
          <div key={item.project} className="flex items-center gap-3">
            <span className="text-sm text-white/60 w-32 truncate">{item.project}</span>
            <div className="flex-1 h-2 bg-white/10 rounded-full overflow-hidden">
              <div
                className="h-full rounded-full"
                style={{ width: `${item.rate}%`, backgroundColor: item.color }}
              ></div>
            </div>
            <span className="text-sm text-white w-10">{item.rate}%</span>
          </div>
        ))}
      </div>
    </div>
  </div>
);

export const ProductShowcase = () => {
  const [activeTab, setActiveTab] = useState('dashboard');
  const { ref, inView } = useInView({ triggerOnce: true, threshold: 0.2 });

  const tabs = [
    { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard, component: DashboardView },
    { id: 'directory', label: 'Directory', icon: Database, component: DirectoryView },
    { id: 'project', label: 'Project', icon: FolderOpen, component: ProjectView },
    { id: 'analytics', label: 'Analytics', icon: BarChart3, component: AnalyticsView },
  ];

  const ActiveComponent = tabs.find((t) => t.id === activeTab)?.component || DashboardView;

  return (
    <section
      id="showcase"
      data-testid="product-showcase-section"
      className="section-dark py-24 md:py-32"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-white mb-4">
            See Searchefy <span className="gradient-text">in Action</span>
          </h2>
          <p className="text-white/60 max-w-2xl mx-auto">
            Explore the powerful features that make link building systematic and trackable.
          </p>
        </motion.div>

        {/* Tab Navigation */}
        <div className="flex justify-center mb-8">
          <div className="inline-flex gap-2 p-1 bg-white/5 rounded-full">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                data-testid={`showcase-tab-${tab.id}`}
                className={`flex items-center gap-2 px-6 py-3 rounded-full text-sm font-medium transition-all ${
                  activeTab === tab.id
                    ? 'bg-gradient-to-r from-blue-500 to-purple-500 text-white'
                    : 'text-white/60 hover:text-white'
                }`}
              >
                <tab.icon size={18} />
                <span className="hidden sm:inline">{tab.label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Tab Content */}
        <motion.div
          key={activeTab}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="max-w-4xl mx-auto"
          data-testid={`showcase-content-${activeTab}`}
        >
          <ActiveComponent />
        </motion.div>
      </div>
    </section>
  );
};

export default ProductShowcase;
